import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { MinusSquareOutlined, PlusSquareOutlined, QuestionCircleOutlined } from "@ant-design/icons";
import { App, Divider, Empty, Popconfirm, Select, Table, Tag, Tooltip } from "antd";
import { observer } from "mobx-react";
import { useCallback, useEffect, useState } from "react";
import { Notices, useTableColumnFilterSimpleInput } from "@devowl-wp/react-cookie-banner-admin";
import { jsxJoin } from "@devowl-wp/react-utils";
import { fetchStatus, useProgress } from "@devowl-wp/real-queue";
import { ConsentRecordBanner } from "./banner.js";
import { ConsentTableRecordExpand } from "./expand.js";
import { useAvailableDateRangesForFilter } from "../../../../hooks/useAvailableDateRangesForFilter.js";
import { useConfigHintText } from "../../../../hooks/useConfigHintText.js";
import { useStores } from "../../../../store/stores.js";
import { DATABASE_CONSENT_MIGRATION_QUEUE_JOB } from "../../../../types/queue.js";
import { __, _i } from "../../../../utils/i18n.js";
import { DateRangePicker } from "../../../dateRangePicker.js";
import { calculatePercentForDatabaseConsentMigrationV2QueueJob } from "../dbConsentMigrationV2Badge.js";
import { ConsentRefererSelect } from "../refererSelect.js";
const { Column } = Table;
const ConsentTable = observer(()=>{
    const { message } = App.useApp();
    const { consentStore, optionStore: { contexts } } = useStores();
    const { busyConsent, pageCollection, perPage, count, truncatedIpsCount, filters: { page: currentPage, referer: currentReferer, context: currentContext, dates: currentDates, ip: currentIp } } = consentStore;
    const [bannerRecord, setBannerRecord] = useState();
    const [replayBannerRecord, setReplayBannerRecord] = useState(false);
    const [migrationNeeded, setMigrationNeeded] = useState(false);
    const [bannerVisible, setBannerVisible] = useState(true);
    const contextKeys = Object.keys(contexts);
    const { percent: percentMigration, currentJob: currentJobMigration, remaining: remainingMigration, errors: errorMigration } = useProgress({
        type: DATABASE_CONSENT_MIGRATION_QUEUE_JOB,
        fetchAdditionalData: DATABASE_CONSENT_MIGRATION_QUEUE_JOB,
        onAdditionalData: ({ [DATABASE_CONSENT_MIGRATION_QUEUE_JOB]: data })=>{
            if (data.migrationNeeded !== migrationNeeded) {
                setMigrationNeeded(data.migrationNeeded);
            }
        }
    });
    // Provide predefined ranges to the user
    const availableRanges = useAvailableDateRangesForFilter();
    const fetchAll = useCallback(async ()=>{
        try {
            await consentStore.fetchAll();
        } catch (e) {
            message.error(e.responseJSON.message);
        }
    }, []);
    // Initially fetch first page
    useEffect(()=>{
        fetchAll();
        fetchStatus(true);
    }, []);
    const hintText = useConfigHintText("list-of-consents");
    const hintTextDeleted = useConfigHintText("consents-deleted");
    const getColumnSearchProps = useTableColumnFilterSimpleInput();
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            bannerRecord && /*#__PURE__*/ _jsx(ConsentRecordBanner, {
                record: bannerRecord,
                visible: bannerVisible,
                replayBannerRecord: replayBannerRecord,
                onClose: ()=>{
                    setBannerVisible(false);
                    setReplayBannerRecord(false);
                    setBannerRecord(undefined);
                }
            }),
            /*#__PURE__*/ _jsxs("div", {
                style: {
                    textAlign: "right",
                    marginBottom: 15
                },
                children: [
                    contextKeys.length > 1 && /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsx("label", {
                                children: /*#__PURE__*/ _jsx(Select, {
                                    style: {
                                        width: 200,
                                        textAlign: "left"
                                    },
                                    value: currentContext,
                                    onSelect: (value)=>{
                                        consentStore.applyPage(1);
                                        consentStore.applyContext(value);
                                        fetchAll();
                                    },
                                    children: contextKeys.map((context)=>/*#__PURE__*/ _jsx(Select.Option, {
                                            value: context,
                                            children: contexts[context]
                                        }, context))
                                })
                            }),
                            /*#__PURE__*/ _jsx(Divider, {
                                type: "vertical"
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("label", {
                        children: [
                            __("Period"),
                            ":",
                            " ",
                            /*#__PURE__*/ _jsx(DateRangePicker, {
                                value: /* Ant design relies on moment 2.27 and typings are currently incompatible with other moment typings */ currentDates,
                                ranges: availableRanges,
                                onChange: (dates)=>{
                                    consentStore.applyPage(1);
                                    consentStore.applyDates(dates || [
                                        undefined,
                                        undefined
                                    ]);
                                    fetchAll();
                                }
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(Divider, {
                        type: "vertical"
                    }),
                    /*#__PURE__*/ _jsx("label", {
                        style: {
                            textAlign: "left"
                        },
                        children: /*#__PURE__*/ _jsx(ConsentRefererSelect, {
                            value: currentReferer,
                            onChange: (referer)=>{
                                consentStore.applyPage(1);
                                consentStore.applyReferer(referer);
                                fetchAll();
                            }
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(Notices, {
                notices: [
                    truncatedIpsCount > 0 && !!currentIp && {
                        message: __("For data protection reasons, IP addresses, depending on the configuration of the cookie banner, were only shortened by the last octet and stored hashed. In this case, you can only assign consents to individual IP addresses with a high probability, but not with absolute certainty. Therefore, also match the time of consent to narrow down your search for the proper consent!"),
                        severity: "warning"
                    },
                    remainingMigration > 0 && percentMigration > 0 && currentJobMigration ? {
                        message: __("Real Cookie Banner v5.0 introduces an optimized database schema that allows consent documents to be stored in less storage space. Previously stored consent data is currently being automatically migrated in the background (%s %%). Please have a little patience!", calculatePercentForDatabaseConsentMigrationV2QueueJob(currentJobMigration.data)),
                        severity: "info"
                    } : migrationNeeded && !errorMigration ? {
                        message: _i(__("Real Cookie Banner v5.0 introduces an optimized database schema that allows consent documents to be stored in less storage space. There are still consents left to migrate. {{a}}Click here to start the migration.{{/a}}"), {
                            a: /*#__PURE__*/ _jsx("a", {
                                onClick: ()=>{
                                    setMigrationNeeded(false);
                                    consentStore.fetchAll({
                                        migration: DATABASE_CONSENT_MIGRATION_QUEUE_JOB
                                    }).then(()=>fetchStatus(true));
                                },
                                children: __("Start migration")
                            })
                        }),
                        severity: "info"
                    } : undefined
                ]
            }),
            /*#__PURE__*/ _jsxs(Table, {
                pagination: {
                    current: currentPage,
                    pageSize: perPage,
                    total: count,
                    showTotal: (total, range)=>`${range[0]}-${range[1]} / ${total}`,
                    showSizeChanger: false
                },
                style: {
                    marginTop: 5
                },
                locale: {
                    emptyText: /*#__PURE__*/ _jsx(Empty, {
                        description: __("No data")
                    })
                },
                loading: busyConsent,
                dataSource: Array.from(pageCollection.values()),
                rowKey: "id",
                size: "small",
                bordered: true,
                expandable: {
                    expandedRowRender: (record)=>/*#__PURE__*/ _jsx("div", {
                            style: {
                                background: "white",
                                padding: 10
                            },
                            children: /*#__PURE__*/ _jsx(ConsentTableRecordExpand, {
                                record: record,
                                onPreview: (r, replay)=>{
                                    setBannerRecord(r);
                                    setReplayBannerRecord(replay);
                                    setBannerVisible(true);
                                }
                            })
                        }),
                    rowExpandable: ()=>true,
                    expandIcon: ({ expanded, onExpand, record })=>/*#__PURE__*/ _jsx(Tag, {
                            style: {
                                cursor: "pointer"
                            },
                            onClick: (e)=>onExpand(record, e),
                            icon: expanded ? /*#__PURE__*/ _jsx(MinusSquareOutlined, {}) : /*#__PURE__*/ _jsx(PlusSquareOutlined, {}),
                            children: expanded ? __("Less") : __("More")
                        })
                },
                onChange: ({ current }, filters)=>{
                    const uuid = filters.uuid?.[0] || "";
                    const ip = filters.ip?.[0] || "";
                    consentStore.applyPage(current);
                    consentStore.applyUuid(uuid);
                    consentStore.applyIp(ip);
                    fetchAll();
                },
                footer: ()=>/*#__PURE__*/ _jsx(Popconfirm, {
                        overlayStyle: {
                            maxWidth: 300
                        },
                        arrow: {
                            pointAtCenter: true
                        },
                        title: _i(__("Are you sure you want to delete all consents? You should only do this if it is absolutely necessary. In case you want to continue, make sure you have {{a}}exported{{/a}} all consents beforehand (for legal reasons)."), {
                            a: /*#__PURE__*/ _jsx("a", {
                                href: "#/import"
                            })
                        }),
                        okText: __("I am sure!"),
                        cancelText: __("Cancel"),
                        placement: "topRight",
                        onConfirm: ()=>window.confirm(__("Just to be sure: Do you really want to delete all consents?")) && consentStore.deleteAll(),
                        children: /*#__PURE__*/ _jsx("button", {
                            className: "button-link",
                            children: __("Delete all consents")
                        })
                    }),
                children: [
                    /*#__PURE__*/ _jsx(Column, {
                        title: __("Time of consent documentation"),
                        dataIndex: "created",
                        render: (value, record)=>{
                            const { created } = record;
                            return /*#__PURE__*/ _jsxs(_Fragment, {
                                children: [
                                    new Date(created).toLocaleString(document.documentElement.lang),
                                    " ",
                                    record.dnt && /*#__PURE__*/ _jsx(Tooltip, {
                                        title: __("This consent was given automatically because the browser sent a 'Do Not Track' header. Accordingly, only essential services have been consented to."),
                                        children: /*#__PURE__*/ _jsx(Tag, {
                                            color: "magenta",
                                            children: __("Do Not Track")
                                        })
                                    }),
                                    record.blocker > 0 && /*#__PURE__*/ _jsx(Tooltip, {
                                        title: __("This consent has been given in a content blocker."),
                                        children: /*#__PURE__*/ _jsx(Tag, {
                                            color: "cyan",
                                            children: __("Content Blocker")
                                        })
                                    }),
                                    record.forwarded > 0 && /*#__PURE__*/ _jsx(Tooltip, {
                                        title: __("This consent was implicitly given by Consent Forwarding."),
                                        children: /*#__PURE__*/ _jsx(Tag, {
                                            color: "green",
                                            children: record.forwarded_blocker ? __("Forwarded through Content Blocker") : __("Forwarded")
                                        })
                                    }),
                                    record.custom_bypass && /*#__PURE__*/ _jsx(Tooltip, {
                                        title: __("This consent was implicitly given by a configured bypass."),
                                        children: /*#__PURE__*/ _jsx(Tag, {
                                            color: "magenta",
                                            children: record.custom_bypass_readable
                                        })
                                    })
                                ]
                            });
                        }
                    }, "created"),
                    /*#__PURE__*/ _jsx(Column, {
                        title: /*#__PURE__*/ _jsx(Tooltip, {
                            title: __("Unique name of the consent given"),
                            children: /*#__PURE__*/ _jsxs("span", {
                                children: [
                                    __("UUID"),
                                    " ",
                                    /*#__PURE__*/ _jsx(QuestionCircleOutlined, {
                                        style: {
                                            color: "#9a9a9a"
                                        }
                                    })
                                ]
                            })
                        }),
                        dataIndex: "uuid",
                        render: (value, record)=>/*#__PURE__*/ _jsx("code", {
                                children: record.uuid
                            }),
                        ...getColumnSearchProps()
                    }, "uuid"),
                    /*#__PURE__*/ _jsx(Column, {
                        title: /*#__PURE__*/ _jsx(Tooltip, {
                            title: __("If you have allowed to log IP addresses, you can view them here. Otherwise you will see a salted hash of the IP address (truncated)."),
                            children: /*#__PURE__*/ _jsxs("span", {
                                children: [
                                    __("IP"),
                                    " ",
                                    /*#__PURE__*/ _jsx(QuestionCircleOutlined, {
                                        style: {
                                            color: "#9a9a9a"
                                        }
                                    })
                                ]
                            })
                        }),
                        dataIndex: "ip",
                        render: (value, record)=>record.ipv4 ? /*#__PURE__*/ _jsxs(_Fragment, {
                                children: [
                                    /*#__PURE__*/ _jsx("code", {
                                        children: record.ipv4
                                    }),
                                    !!currentIp && record.ipv4 === currentIp && /*#__PURE__*/ _jsx(Tag, {
                                        style: {
                                            marginLeft: 5
                                        },
                                        color: "blue",
                                        children: __("Exact match")
                                    })
                                ]
                            }) : record.ipv6 ? /*#__PURE__*/ _jsxs(_Fragment, {
                                children: [
                                    /*#__PURE__*/ _jsx("code", {
                                        children: record.ipv6
                                    }),
                                    !!currentIp && record.ipv6 === currentIp && /*#__PURE__*/ _jsx(Tag, {
                                        style: {
                                            marginLeft: 5
                                        },
                                        color: "blue",
                                        children: __("Exact match")
                                    })
                                ]
                            }) : record.ipv4_hash ? /*#__PURE__*/ _jsx(Tooltip, {
                                title: record.ipv4_hash,
                                children: /*#__PURE__*/ _jsx("code", {
                                    children: record.ipv4_hash.slice(0, 8)
                                })
                            }) : /*#__PURE__*/ _jsx(Tooltip, {
                                title: record.ipv6_hash,
                                children: /*#__PURE__*/ _jsx("code", {
                                    children: record.ipv6_hash.slice(0, 8)
                                })
                            }),
                        ...getColumnSearchProps()
                    }, "ip"),
                    /*#__PURE__*/ _jsx(Column, {
                        title: __("Accepted services"),
                        dataIndex: "decision",
                        render: (value, record)=>record.decision_labels.map((label)=>/*#__PURE__*/ _jsx(Tag, {
                                    children: label
                                }, label))
                    }, "decision"),
                    /*#__PURE__*/ _jsx(Column, {
                        title: __("Actions"),
                        dataIndex: "actions",
                        render: (value, record)=>{
                            return jsxJoin([
                                /*#__PURE__*/ _jsx(Popconfirm, {
                                    title: __("Are you sure you want to delete this entry?"),
                                    placement: "bottomLeft",
                                    onConfirm: ()=>record.delete(),
                                    okText: __("Delete"),
                                    cancelText: __("Cancel"),
                                    overlayStyle: {
                                        maxWidth: 350
                                    },
                                    children: /*#__PURE__*/ _jsx("a", {
                                        children: __("Delete")
                                    })
                                }, "delete")
                            ], /*#__PURE__*/ _jsx(Divider, {
                                type: "vertical"
                            }));
                        }
                    }, "actions")
                ]
            }),
            /*#__PURE__*/ _jsx("p", {
                className: "description",
                style: {
                    maxWidth: 800,
                    margin: "30px auto 0",
                    textAlign: "center"
                },
                children: hintText
            }),
            /*#__PURE__*/ _jsx("p", {
                className: "description",
                style: {
                    maxWidth: 800,
                    margin: "30px auto 0",
                    textAlign: "center"
                },
                children: hintTextDeleted
            })
        ]
    });
});
export { ConsentTable };
